if (!customElements.get('testimonials-component')) {
  class Testimonials extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        sliderWrapper: '.testimonials__items',
        pagination: '.swiper-pagination',
        nextEl: '.swiper-btn-next',
        prevEl: '.swiper-btn-prev',
      };
      this.classes = {
        grid: 'f-grid',
        swiper: 'swiper',
        swiperWrapper: 'swiper-wrapper',
      };

      this.sectionId = this.dataset.sectionId;
      this.section = this.closest(`.section-${this.sectionId}`);
      this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

      this.enableSlider = this.dataset.enableSlider === 'true';
      this.items = parseInt(this.dataset.items);
      this.tabletItems = parseInt(this.dataset.tabletItems);
      this.mobileItems = parseInt(this.dataset.mobileItems) || 1;

      this.sliderInstance = false;

      if (!this.enableSlider) return;

      this.init();
      document.addEventListener('matchMobile', () => {
        this.init();
      });
      document.addEventListener('unmatchMobile', () => {
        this.init();
      });
    }

    // Always initialize the slider; mobile uses 1-up view but still needs navigation
    init() {
      // Idempotent init: avoid re-init of active instances
      if (this.sliderInstance?.slider && !this.sliderInstance.slider.destroyed) return;
      
      this.initSlider();
    }

    initSlider() {
      if (typeof this.sliderInstance !== 'object') {
        // Read gap before class swap, convert rem→px (×16), fallback to 8px
        let preGridGap = window.getComputedStyle(this.sliderWrapper).gap;
        // if shorthand like "8px 8px", take the first value
        if (preGridGap && preGridGap.includes(' ')) preGridGap = preGridGap.split(' ')[0];

        let preGridSpace = 0;
        if (preGridGap && preGridGap.includes('rem')) {
          preGridSpace = parseFloat(preGridGap) * 16; // rem→px
        } else if (preGridGap && preGridGap.includes('px')) {
          preGridSpace = parseFloat(preGridGap);
        }
        if (!preGridSpace || Number.isNaN(preGridSpace)) preGridSpace = 8; // fallback

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);
        
        let spaceBetween = preGridSpace;

        // Mobile items & gap safety
        if (!spaceBetween || Number.isNaN(spaceBetween)) spaceBetween = 8;
        spaceBetween = Math.max(8, spaceBetween);

        // Left gutter from .page-width
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        let leftGutterPx = 0;
        if (isMobile) {
          const page = this.closest('.page-width');
          const pl = page ? parseFloat(getComputedStyle(page).paddingLeft) : 0;
          leftGutterPx = Number.isFinite(pl) && pl > 0 ? pl : 24; // fallback to 24px (1.5rem)
        }

        // Loop safety by slide count
        const slideCount = this.querySelectorAll('.swiper-slide').length;
        const loopEnabled = !isMobile && slideCount > 1;

        const sliderOptions = {
          modules: [FoxTheme.Swiper.Navigation, FoxTheme.Swiper.Pagination],
          slidesPerView: isMobile ? (parseInt(this.mobileItems, 10) || 1) : (parseInt(this.items, 10) || 2),
          spaceBetween: spaceBetween,
          navigation: {
            nextEl: this.section.querySelector(this.selectors.nextEl),
            prevEl: this.section.querySelector(this.selectors.prevEl),
          },
          pagination: false,
          loop: loopEnabled,
          initialSlide: 0,
          slidesOffsetBefore: isMobile ? leftGutterPx : 0,
          observer: true,
          observeParents: true,
          breakpoints: {
            768: {
              slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems, 10),
            },
            1280: {
              slidesPerView: parseInt(this.items, 10),
            },
          },
          threshold: 2,
        };

        this.sliderInstance = new window.FoxTheme.Carousel(this, sliderOptions);
        this.sliderInstance.init();

        // Deferred and guarded slide navigation
        const s = this.sliderInstance?.slider;
        if (isMobile && s) {
          requestAnimationFrame(() => {
            if (s.slides && s.slides.length > 0) {
              s.update();
              s.slideTo(0, 0);
            }
          });
        }

        if (window?.Shopify?.designMode) {
          const nav = sliderOptions?.navigation || {};
          if (!nav.nextEl || !nav.prevEl) {
            console.warn('[testimonials] navigation elements missing for section', this.section?.dataset?.sectionId || this.sectionId);
          }
        }

        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
          });
        });
      }
      if (Shopify.designMode && typeof this.sliderInstance === 'object') {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.detail.sectionId != this.sectionId) return;
          let { target } = e;
          const index = Number(target.dataset.index);

          this.sliderInstance.slider.slideToLoop(index);
        });
      }
    }

    destroySlider() {
      this.classList.remove(this.classes.swiper);
      this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
      this.sliderWrapper.classList.add(this.classes.grid);
      if (typeof this.sliderInstance === 'object') {
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }
    }
  }
  customElements.define('testimonials-component', Testimonials);
}
